<template>
	<div>
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>
		<v-container grid-list-xl pa-0 class="text-xs-left">
			<loading-indicator v-if="isLoading" loading-text="Loading Compliance..." class="ml-3" />

			<div v-else>
				<div v-if="employeeCompliance">
					<v-alert :value="employeeCompliance.isCompliant" color="success" icon="mdi-checkbox-marked-outline" class="mb-3 text-xs-left">
						<span v-if="isLoggedInEmployee">There are no outstanding compliance items for you to complete</span>
						<span v-else>There are no outstanding compliance items</span>
					</v-alert>
					<v-alert :value="!employeeCompliance.isCompliant" color="error" icon="mdi-alert-circle-outline" class="mb-3 text-xs-left">
						<span v-if="isLoggedInEmployee"
							>You have outstanding compliance items. <br /><span class="caption"
								>It is your responsibility to be compliant and to ensure company policies are adhered to and it is your responsibility to acknowledge your roles and responsibilities within the business.</span
							></span
						>
						<span v-else>This employee has outstanding compliance items</span>
					</v-alert>
				</div>

				<v-expansion-panel v-if="employeeCompliance" expand class="employee-compliance-checklist" v-model="expansionPanel">
					<v-expansion-panel-content v-if="$_hasAccessToModule('positionDescriptions')" :disabled="employeeCompliance.unacknowledgedPD == null">
						<template v-slot:header>
							<v-list :two-line="!employeeCompliance.isCompliant">
								<v-list-tile>
									<v-list-tile-action>
										<v-icon :color="pdAcknowledgedRow.iconColor != null ? pdAcknowledgedRow.iconColor : ''">{{ pdAcknowledgedRow.icon }}</v-icon>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title>{{ pdAcknowledgedRow.title }}</v-list-tile-title>
										<v-list-tile-sub-title v-html="pdAcknowledgedRow.subTitle" class="sub-title"></v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>
							</v-list>
						</template>

						<unacknowledged-position-description-list
							class="mb-3"
							:unacknowledged-positions="employeeCompliance.unacknowledgedPD != null ? [employeeCompliance.unacknowledgedPD] : []"
							:employeeId="employeeId"
							:is-logged-in-employee="isLoggedInEmployee"
							@onUpdated="onPositionsUpdated"
						>
							<template slot="heading">
								<p class="px-3 pt-3 pb-1">{{ unacknowledgedPdHeaderMessage }}</p>
							</template>
						</unacknowledged-position-description-list>
					</v-expansion-panel-content>
					<v-expansion-panel-content :disabled="employeeCompliance.unacknowledgedHrDocuments.length === 0">
						<template v-slot:header>
							<v-list two-line>
								<v-list-tile>
									<v-list-tile-action>
										<v-icon :color="policiesAcknowledgedRow.iconColor != null ? policiesAcknowledgedRow.iconColor : ''">{{ policiesAcknowledgedRow.icon }}</v-icon>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title v-html="policiesAcknowledgedRow.title"></v-list-tile-title>
										<v-list-tile-sub-title v-html="policiesAcknowledgedRow.subTitle" class="sub-title"></v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>
							</v-list>
						</template>

						<unacknowledged-hr-documents-list
							v-if="employeeCompliance.unacknowledgedHrDocuments.length > 0"
							class="mb-3"
							:hr-documents="employeeCompliance.unacknowledgedHrDocuments"
							:is-logged-in-employee="isLoggedInEmployee"
							title="Unacknowledged Policies"
							@onUpdated="onHrDocumentsUpdated"
						>
							<template slot="heading">
								<p class="px-3 pt-3 pb-1">{{ unacknowledgedPoliciesHeaderMessage }}</p>
							</template>
						</unacknowledged-hr-documents-list>

						<a
							v-if="employeeCompliance.unacknowledgedHrDocuments.length > 0 && employeeCompliance.acknowledgedHrDocuments.length > 0 && !showAcknowledgedPolicies"
							href="#"
							class="my-4 d-block"
							@click.prevent.stop="showAcknowledgedPolicies = true"
							>View acknowledged policies</a
						>

						<acknowledged-hr-documents-list
							v-if="employeeCompliance.acknowledgedHrDocuments.length > 0 && (employeeCompliance.unacknowledgedHrDocuments.length === 0 || showAcknowledgedPolicies)"
							class="my-3"
							:hr-documents="employeeCompliance.acknowledgedHrDocuments"
							:is-logged-in-employee="isLoggedInEmployee"
							title="Acknowledged Policies"
							@onUpdated="onHrDocumentsUpdated"
						>
							<template slot="heading" v-if="employeeCompliance.unacknowledgedHrDocuments.length > 0">
								<p class="px-3 pt-3 pb-1">{{ acknowledgedPoliciesHeaderMessage }}</p>
							</template>
						</acknowledged-hr-documents-list>
					</v-expansion-panel-content>
					<v-expansion-panel-content v-if="$_hasAccessToModule('compliance') && employeeCompliance.certificationCount > 0" :disabled="employeeCompliance.expiredCertifications.length === 0">
						<template v-slot:header>
							<v-list two-line>
								<v-list-tile>
									<v-list-tile-action>
										<v-icon :color="certsRow.iconColor != null ? certsRow.iconColor : ''">{{ certsRow.icon }}</v-icon>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title v-html="certsRow.title"></v-list-tile-title>
										<v-list-tile-sub-title v-html="certsRow.subTitle" class="sub-title"></v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>
							</v-list>
						</template>
						<ul v-if="employeeCompliance.expiredCertifications.length > 0" class="py-4">
                            <li v-for="expiredCert in employeeCompliance.expiredCertifications" :key="certificationId">
                                {{ expiredCert.name }}
                            </li>
                        </ul>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</div>
		</v-container>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import employeeComplianceApi from '@/api/employeeCompliance.api';
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import UnacknowledgedHrDocumentsList from '@/components/compliance/UnacknowledgedHrDocumentsList';
	import AcknowledgedHrDocumentsList from '@/components/compliance/AcknowledgedHrDocumentsList';
	import UnacknowledgedPositionDescriptionList from '@/components/compliance/UnacknowledgedPositionDescriptionList';

	export default {
		components: {
			LoadingIndicator,
			UnacknowledgedHrDocumentsList,
			UnacknowledgedPositionDescriptionList,
			AcknowledgedHrDocumentsList
		},
		props: {
			employeeId: {
				type: String,
				required: true
			},
			triggerRefreshId: {
				type: String,
				required: false
			}
		},
		data() {
			return {
				employeeCompliance: null,
				isLoading: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				expansionPanel: [true, true, true],
				showAcknowledgedPolicies: false
			};
		},
		async created() {
			await this.loadCompliance();
		},
		watch: {
			async triggerRefreshId() {
				await this.loadCompliance();
			}
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee'
			}),
			isLoggedInEmployee() {
				return this.employeeId === this.loggedInEmployee.id;
			},
			unacknowledgedPdHeaderMessage() {
				if (this.employeeCompliance == null || this.employeeCompliance.unacknowledgedPD == null) return;

				const isUpdate = this.employeeCompliance.unacknowledgedPD.isUpdate;

				if (this.isLoggedInEmployee) {
					if (isUpdate) return 'An update has been made to your position description that requires your acknowledgement';
					else return 'Your position description requires your acknowledgement';
				} else {
					if (isUpdate) return 'An update has been made to the position description that requires acknowledgement from the employee';
					else return 'The following position description requires acknowledgement from the employee:';
				}
			},
			unacknowledgedPoliciesHeaderMessage() {
				if (!this.employeeCompliance) return;

				if (this.isLoggedInEmployee) {
					return 'The following policies require your acknowledgement:';
				} else {
					return 'The following policies require acknowledgement:';
				}
			},
			acknowledgedPoliciesHeaderMessage() {
				if (!this.employeeCompliance) return;

				return 'The following policies have been acknowledged:';
			},
			pdAcknowledgedRow() {
				if (this.employeeCompliance.livePD == null) {
					return {
						icon: 'mdi-minus-circle-outline',
						title: 'No position description has been setup'
					};
				} else if (this.employeeCompliance.unacknowledgedPD !== null) {
					return {
						icon: 'mdi-alert-circle-outline',
						iconColor: 'error',
						title: 'Position description requires acknowledgement',
						subTitle: this.employeeCompliance.unacknowledgedPD.positionTitle
					};
				} else {
					return {
						icon: 'mdi-checkbox-marked-outline',
						iconColor: 'success',
						title: 'Position description acknowledged',
						subTitle: `<strong>${this.employeeCompliance.livePD.positionTitle}</strong> (acknowledged on ${this.$options.filters.formatDateTime(this.employeeCompliance.livePD.acknowledgedDateUtc)})`
					};
				}
			},
			policiesAcknowledgedRow() {
				const relevantToEmployeeCount = this.employeeCompliance.hrDocumentsRelevantToEmployeeCount;
				const unacknowledgedCount = this.employeeCompliance.unacknowledgedHrDocuments.length;
				const acknowledgedCount = this.employeeCompliance.hrDocumentsRelevantToEmployeeCount - unacknowledgedCount;

				const allAcknowledged = acknowledgedCount === relevantToEmployeeCount;
				return {
					icon: allAcknowledged ? 'mdi-checkbox-marked-outline' : 'mdi-alert-circle-outline',
					iconColor: allAcknowledged ? 'success' : 'error',
					title: `<strong>${acknowledgedCount}/${relevantToEmployeeCount}</strong> policies acknowledged`,
					subTitle: allAcknowledged
						? 'All required policies have been acknowledged'
						: `<strong>${unacknowledgedCount}</strong> ${this.getPoliciesTextPlural(unacknowledgedCount)} ${unacknowledgedCount === 1 ? 'requires' : 'require'} acknowledgement`
				};
			},
			certsRow() {
				const expiredCount = this.employeeCompliance.expiredCertifications.length;

				if (expiredCount > 0) {
					return {
						icon: 'mdi-alert-circle-outline',
						iconColor: 'error',
						title: `<strong>${expiredCount}</strong> ${this.getCertificationsTextPlural(expiredCount)} ${expiredCount === 1 ? 'has' : 'have'} expired`,
						subTitle: ''
					};
				} else {
					return {
						icon: 'mdi-checkbox-marked-outline',
						iconColor: 'success',
						title: 'All certifications are valid',
						subTitle: ''
					};
				}
			}
		},
		methods: {
			async loadCompliance() {
				try {
					this.isLoading = true;
					this.employeeCompliance = await employeeComplianceApi.get(this.employeeId);
					this.$store.dispatch('setPageTitle', `${this.employeeCompliance.employee.firstName} ${this.employeeCompliance.employee.lastName}`);
					this.expansionPanel = [this.employeeCompliance.unacknowledgedPD !== null, this.employeeCompliance.unacknowledgedHrDocuments.length > 0, this.employeeCompliance.expiredCertifications.length > 0];
				} catch (ex) {
					this.showSnackbar('Failed to retrieve Employee Compliance data', true);
				} finally {
					this.isLoading = false;
				}
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			getPoliciesTextPlural(count) {
				return count === 1 ? 'policy' : 'policies';
			},
			getCertificationsTextPlural(count) {
				return count === 1 ? 'certification' : 'certifications';
			},
			onPositionsUpdated(message) {
				if (message) {
					this.showSnackbar(message);
				}
				this.loadCompliance();

				this.$emit('onUpdated');
			},
			onHrDocumentsUpdated(message) {
				if (message) {
					this.showSnackbar(message);
				}
				this.loadCompliance();

				this.$emit('onUpdated');
			}
		}
	};
</script>

<style>
	.employee-compliance-checklist .v-expansion-panel__body {
		padding-left: 80px;
	}
	.employee-compliance-checklist .v-expansion-panel__header:hover,
	.employee-compliance-checklist .v-expansion-panel__header:hover .v-list {
		background-color: rgb(244 244 244);
	}
</style>
